import {
  AfterViewInit,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {NgModel} from '@angular/forms';
import {OptionModel} from '../../../../../../models/option-model';
import {PumpSensorNameEnum} from '../../../../../../enums/pump-sensor-name-enum';
import {PumpSensorHelperModel} from '../../../../../../models/pump-sensor-helper-model';
import {PumpSensorOptionEnum} from '../../../../../../enums/pump-sensor-option-enum';
import {Subscription} from 'rxjs';
import {AutoSaveService} from '../../../../../../services/auto-save.service';
import {DeviceDataService} from '../../../../../../services/device-data.service';
import {PumpPressureSensorIdService} from '../../../../../../services/pump-pressure-sensor-id.service';
import {debounceTime} from 'rxjs/operators';
import {ShareService} from '../../../../../../services/share.service';
import {ValidationService} from '../../../../../../services/validation.service';
import {SensorsPumpModel} from '../../../../../../models/data-models/sensors-pump-model';

@Component({
  selector: 'app-install-sensors-pump',
  templateUrl: './install-sensors-pump.component.html',
  styleUrls: ['./install-sensors-pump.component.scss'],

})
export class InstallSensorsPumpComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  public model: SensorsPumpModel;

  public readonly LOW_IMAGE_QUALITY = AutoSaveService.LOW_IMAGE_QUALITY;

  public sensorNameOptions: OptionModel[] = [];
  public sensorIdOptions: OptionModel[] = [];

  private readonly MIN_INDEX = 1;

  private MAX_NUMBER_OF_SINGLES: number;

  public sensorTypeOptionsIoTMaxx: OptionModel[] = [
    {value: 10, label: '10'},
    {value: 20, label: '20'}
  ];
  public sensorTypeOptionsJuconn: OptionModel[] = [
    {value: 1005, label: '5A zweiadrig'},
    {value: 1010, label: '10A zweiadrig'},
    {value: 1020, label: '20A zweiadrig'},
    {value: 10, label: '10A dreiadrig'},
    {value: 20, label: '20A dreiadrig'}
  ];

  private inputSubscriptions: Subscription[] = [];

  private isInitCalled = false;
  private pendingChanges: SimpleChanges;

  @ViewChildren(NgModel) dynamicInputs: QueryList<NgModel>;
  private deviceDataSub: Subscription;
  private modelSub: Subscription;

  constructor(
    private deviceDataService: DeviceDataService,
    private pumpPressureSensorIdService: PumpPressureSensorIdService,
    private shareService: ShareService,
    public validationService: ValidationService
  ) {
    this.deviceDataSub = this.deviceDataService.deviceData$.subscribe(data => {
      if (data?.deviceData) {
        this.MAX_NUMBER_OF_SINGLES = this.pumpPressureSensorIdService.getMaxNumberOfSingles();
        this.initOptions();
      }
    });

    this.pumpPressureSensorIdService.allSensorIdOptionsSubject$.subscribe(() => {
      this.sensorIdOptions = this.pumpPressureSensorIdService.getPumpSensorIdOptions();
    });
    this.pumpPressureSensorIdService.pressureSensorIdOptionsSubject$.subscribe(() => {
      this.sensorIdOptions = this.pumpPressureSensorIdService.getPumpSensorIdOptions();
    });

    this.modelSub = this.shareService.sensorsPump$.subscribe(data => {
      this.model = data || new SensorsPumpModel();
    });
    this.model = this.shareService.sensorsPump || new SensorsPumpModel();
  }

  ngOnDestroy(): void {
    this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.deviceDataSub?.unsubscribe();
    this.modelSub?.unsubscribe();
  }


  ngOnInit(): void {
    this.isInitCalled = true;
    if (this.pendingChanges) {
      this.handleChanges(this.pendingChanges);
      this.pendingChanges = null;
    }

    this.sensorIdOptions = this.pumpPressureSensorIdService.getPumpSensorIdOptions();
  }


  ngAfterViewInit(): void {
    this.dynamicInputs.forEach(input => {
      this.inputSubscriptions.push(input.control.valueChanges.pipe(
        debounceTime(500)
      ).subscribe(() => {
        this.publishModel();
      }));
    });

    this.dynamicInputs.changes.subscribe(() => {
      this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());

      this.dynamicInputs.forEach(input => {
        this.inputSubscriptions.push(input.control.valueChanges.pipe(
          debounceTime(500)
        ).subscribe(() => {
          this.publishModel();
        }));
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isInitCalled) {
      this.handleChanges(changes);
    } else {
      this.pendingChanges = changes;
    }
  }

  public updateSensorId(value: any, sensor: PumpSensorHelperModel) {
    sensor.sensorId = value;
    this.pumpPressureSensorIdService.setPumpSensorIdOptions(this.collectPumpSensorIds());
  }

  private handleChanges(changes: SimpleChanges) {
    this.updateSensorNamesOptionsVisibility();
  }

  private initOptions() {
    this.prepareSingleSensor(PumpSensorNameEnum.CIR_PUMP, PumpSensorOptionEnum.CIR_PUMP);
    this.prepareSingleSensor(PumpSensorNameEnum.ROOM_PUMP, PumpSensorOptionEnum.ROOM_PUMP);
    this.prepareSingleSensor(PumpSensorNameEnum.BUFFER_PUMP, PumpSensorOptionEnum.BUFFER_PUMP);
    this.prepareSingleSensor(PumpSensorNameEnum.BOILER_PUMP, PumpSensorOptionEnum.BOILER_PUMP);
    this.prepareSingleSensor(PumpSensorNameEnum.SOLAR_PUMP_PRIMARY, PumpSensorOptionEnum.SOLAR_PUMP_PRIMARY);
    this.prepareSingleSensor(PumpSensorNameEnum.SOLAR_PUMP_SECONDARY, PumpSensorOptionEnum.SOLAR_PUMP_SECONDARY);
    this.prepareSingleSensor(PumpSensorNameEnum.HOT_WATER_CHARGING_PUMP, PumpSensorOptionEnum.HOT_WATER_CHARGING_PUMP);
    this.prepareSingleSensor(PumpSensorNameEnum.HEAT_PUMP, PumpSensorOptionEnum.HEAT_PUMP);
    this.prepareSingleSensor(PumpSensorNameEnum.WARM_WATER_PUMP, PumpSensorOptionEnum.WARM_WATER_PUMP);

    this.sensorNameOptions = this.sensorNameOptions.sort((a, b) => a.label.localeCompare(b.label));
  }


  isSameTypeButNotEqualName(index: number, option: OptionModel): boolean {
    const helper = this.model.pumpSensors[index];
    if (helper.name) {
      return helper.type === option.sensorType && helper.index != option.index;
    }
    return false;
  }

  onSensorNameChanged($event: string, helperIndex: number) {
    setTimeout(_ => {
      const index = this.parseIndexOutOfName($event);

      this.model.pumpSensors[helperIndex].index = index;
      this.model.pumpSensors[helperIndex].name = $event;
      this.model.pumpSensors[helperIndex].type = this.parseTypeOutOfName($event);

      this.updateSensorNamesOptionsVisibility();
      this.dynamicInputs.forEach(dInput => dInput.control.updateValueAndValidity());

    }, 0);
  }


  onRemoveBtnClicked(index: number) {
    this.model.pumpSensors.splice(index, 1);
    this.updateSensorNamesOptionsVisibility();
    this.pumpPressureSensorIdService.setPumpSensorIdOptions(this.collectPumpSensorIds());
    this.publishModel();
  }

  onAddSensorBtnClicked() {
    const sensor = new PumpSensorHelperModel();
    sensor.sensorType = 1005;
    sensor.pumpBrand = null;
    sensor.pumpType = null;
    sensor.pumpElectronicallyControlled = false;
    this.model.pumpSensors.push(sensor);
    this.publishModel();
  }

  showError(model: NgModel) {
    return model.control.touched && model.control.invalid;
  }

  private updateSensorNamesOptionsVisibility() {

    const currentIndices = {
      cir_pump: 1,
      room_pump: 1,
      buffer_pump: 1,
      boiler_pump: 1,
      solar_pump_primary: 1,
      solar_pump_secondary: 1,
      hot_water_charging_pump: 1,
      heat_pump: 1,
      warm_water_pump: 1
    };

    this.model.pumpSensors.forEach(sensorHelper => {
      if (currentIndices[sensorHelper.type] !== sensorHelper.index) {
        if (sensorHelper.name) {
          sensorHelper.name = sensorHelper.name.replace(sensorHelper.index + '', currentIndices[sensorHelper.type] + '');
        }
        sensorHelper.index = currentIndices[sensorHelper.type];
      }
      currentIndices[sensorHelper.type]++;
    });

    this.sensorNameOptions.filter(item => item.canBeActivated).forEach(option => {
      if (option.index === currentIndices[option.sensorType]) {
        option.disabled = false;
      } else {
        option.disabled = true;
      }
    });

    this.sensorNameOptions = this.sensorNameOptions.sort((a, b) => a.label.localeCompare(b.label));
  }

  public getFilteredSensorIds(index: number) {
    const filteredModel: OptionModel[] = [];
    if (this.sensorIdOptions?.length > 0) {
      this.sensorIdOptions.forEach((option) => {
        filteredModel.push(option);
      });
    }
    for (let i = 0; i < this.model.pumpSensors.length; i++) {
      if (i !== index) {
        const sensorIdToRemove = filteredModel.findIndex((option) => option.value === this.model.pumpSensors[i].sensorId);
        if (sensorIdToRemove !== -1) {
          filteredModel.splice(sensorIdToRemove, 1);
        }
      }
    }
    return filteredModel;
  }

  private parseTypeOutOfName(name: string): string {
    if (name) {
      const nameArray = name.split('_');
      nameArray.splice(nameArray.length - 1, 1);
      return nameArray.join('_');
    }
    return null;
  }

  private parseIndexOutOfName(name: string): number {
    if (name) {
      const nameArray = name.split('_');
      return Number.parseInt(nameArray[nameArray.length - 1], 10);
    }
    return null;
  }

  private prepareSingleSensor(nameEnum: PumpSensorNameEnum, optionEnum: PumpSensorOptionEnum) {
    for (let i = this.MIN_INDEX; i <= this.MAX_NUMBER_OF_SINGLES; i++) {
      this.sensorNameOptions.push({
        label: nameEnum + i,
        value: optionEnum + '_' + i,
        disabled: i !== this.MIN_INDEX,
        canBeActivated: true,
        index: i,
        sensorType: optionEnum
      });
    }
  }

  private collectPumpSensorIds(): OptionModel[] {
    const usedSensorIds: OptionModel[] = [];
    if (this.model.pumpSensors?.length > 0) {
      this.model.pumpSensors.forEach(sensor => {
        usedSensorIds.push({value: sensor.sensorId, label: sensor.sensorId});
      });
    }
    return usedSensorIds;
  }

  private publishModel(): void {
    this.shareService.sensorsPump = this.model;
  }
}
